// Debug logging utility
// Enable with ?debug=true in URL

class DebugLogger {
  private enabled: boolean = false
  private startTime: number = Date.now()

  constructor() {
    this.enabled = new URLSearchParams(window.location.search).has('debug')

    if (this.enabled) {
      console.log('%c[DEBUG MODE ENABLED]', 'background: #4ecca3; color: #1a1a2e; padding: 4px 8px; font-weight: bold;')
      this.createDebugPanel()
    }
  }

  private createDebugPanel(): void {
    const panel = document.createElement('div')
    panel.id = 'debug-panel'
    panel.style.cssText = `
      position: fixed;
      bottom: 10px;
      right: 10px;
      width: 400px;
      max-height: 300px;
      overflow-y: auto;
      background: rgba(0,0,0,0.9);
      color: #0f0;
      font-family: monospace;
      font-size: 11px;
      padding: 10px;
      border-radius: 8px;
      z-index: 10000;
      border: 1px solid #4ecca3;
    `
    panel.innerHTML = '<div style="color: #4ecca3; margin-bottom: 8px; font-weight: bold;">Debug Log</div>'
    document.body.appendChild(panel)
  }

  private getTimestamp(): string {
    const elapsed = Date.now() - this.startTime
    return `+${(elapsed / 1000).toFixed(2)}s`
  }

  private appendToPanel(message: string, type: 'info' | 'success' | 'error' | 'warn' = 'info'): void {
    const panel = document.getElementById('debug-panel')
    if (!panel) return

    const colors = {
      info: '#aaa',
      success: '#4ecca3',
      error: '#ff6b6b',
      warn: '#ffd93d'
    }

    const line = document.createElement('div')
    line.style.cssText = `color: ${colors[type]}; margin: 2px 0; word-break: break-all;`
    line.textContent = `[${this.getTimestamp()}] ${message}`
    panel.appendChild(line)
    panel.scrollTop = panel.scrollHeight
  }

  log(category: string, message: string, data?: unknown): void {
    if (!this.enabled) return

    const fullMessage = `[${category}] ${message}`
    console.log(`%c${fullMessage}`, 'color: #aaa', data !== undefined ? data : '')
    this.appendToPanel(`${category}: ${message}`, 'info')
  }

  success(category: string, message: string, data?: unknown): void {
    if (!this.enabled) return

    const fullMessage = `[${category}] ✓ ${message}`
    console.log(`%c${fullMessage}`, 'color: #4ecca3', data !== undefined ? data : '')
    this.appendToPanel(`${category}: ✓ ${message}`, 'success')
  }

  error(category: string, message: string, error?: unknown): void {
    // Always log errors, even if debug is disabled
    const fullMessage = `[${category}] ✗ ${message}`
    console.error(fullMessage, error)

    if (this.enabled) {
      this.appendToPanel(`${category}: ✗ ${message}`, 'error')
      if (error instanceof Error) {
        this.appendToPanel(`  → ${error.message}`, 'error')
      }
    }
  }

  warn(category: string, message: string, data?: unknown): void {
    if (!this.enabled) return

    const fullMessage = `[${category}] ⚠ ${message}`
    console.warn(fullMessage, data !== undefined ? data : '')
    this.appendToPanel(`${category}: ⚠ ${message}`, 'warn')
  }

  group(label: string): void {
    if (!this.enabled) return
    console.group(label)
  }

  groupEnd(): void {
    if (!this.enabled) return
    console.groupEnd()
  }

  table(data: unknown): void {
    if (!this.enabled) return
    console.table(data)
  }

  isEnabled(): boolean {
    return this.enabled
  }

  /**
   * Re-check URL params and reinitialize if debug flag is now present
   * Called after OAuth callback restores URL params
   */
  reinitialize(): void {
    const wasEnabled = this.enabled
    this.enabled = new URLSearchParams(window.location.search).has('debug')

    if (this.enabled && !wasEnabled) {
      console.log('%c[DEBUG MODE ENABLED]', 'background: #4ecca3; color: #1a1a2e; padding: 4px 8px; font-weight: bold;')
      this.createDebugPanel()
    }
  }
}

export const debug = new DebugLogger()
